import { useUsers } from '../hooks/useUsers'
import type { UserOption } from '../types'

// AssigneePicker — shared user-picker bound to dbo.Users.id. Two render
// shapes:
//
//   variant="field"  — labelled form control (matches ManagerPicker
//                      visually). Use inside editor modals and forms.
//
//   variant="inline" — bare select that lives inside a table cell or
//                      compact UI. No external label.
//
// Both variants share the same data source (useUsers / /api/users,
// cached 5min) and the same null-handling: value === null is the
// "unassigned" sentinel and round-trips cleanly through the wire
// (`assigneeId: null`).
//
// Callers own the default: when a feature wants Charlotte (userId 2)
// as the fallback for new rows, the call site seeds the initial state.
// Don't bake the default into this component — call sites can use a
// different default (rostered user, last actor, etc.) and the picker
// shouldn't know.

interface Props {
  value: number | null
  onChange: (userId: number | null) => void
  variant?: 'field' | 'inline'
  label?: string           // shown on `field`, used as aria-label on `inline`
  disabled?: boolean
}

export function AssigneePicker({
  value,
  onChange,
  variant = 'field',
  label = 'Assignee',
  disabled
}: Props) {
  const { data: users, isLoading, error } = useUsers()

  // Native <select> needs a string value; coerce at the boundary.
  const selectValue = value == null ? '' : String(value)
  const handleChange = (raw: string) => {
    onChange(raw === '' ? null : Number(raw))
  }

  // `inline`: bare select. Sized to fit a table cell. Italic-soft when
  // unassigned so the "no owner" state is visually quiet but still
  // affordant — the cell is clickable and reveals the native dropdown.
  if (variant === 'inline') {
    return (
      <select
        value={selectValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || isLoading || !!error}
        aria-label={label}
        className={
          'w-full bg-transparent text-xs px-1.5 py-1 rounded border border-transparent ' +
          'hover:border-line hover:bg-white focus:outline-none focus:ring-2 focus:ring-green/30 ' +
          'cursor-pointer disabled:cursor-not-allowed ' +
          (value == null ? 'text-ink-soft italic' : 'text-ink')
        }
      >
        <option value="">— Unassigned —</option>
        {users?.map((u) => (
          <option key={u.id} value={u.id}>
            {formatOption(u)}
          </option>
        ))}
      </select>
    )
  }

  // `field`: labelled form control. Visual parity with ManagerPicker.
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
          {label}
        </span>
        {isLoading && <span className="text-xs text-ink-soft font-mono">loading…</span>}
      </div>
      <select
        value={selectValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || isLoading || !!error}
        className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green focus:ring-1 focus:ring-green/30 transition-colors disabled:opacity-50"
      >
        <option value="">— Unassigned —</option>
        {users?.map((u) => (
          <option key={u.id} value={u.id}>
            {formatOption(u)}
          </option>
        ))}
      </select>
      {error && (
        <div className="text-xs text-oxblood mt-1">
          Could not load users: {(error as Error).message}
        </div>
      )}
    </label>
  )
}

function formatOption(u: UserOption): string {
  const name = u.name ?? `(unnamed user #${u.id})`
  return u.avatarInitials ? `${name} (${u.avatarInitials})` : name
}
